import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import axios, { type AxiosError } from "axios";
import {
  type AdminListUser,
  type AdminUsersApiV2AdminUsersUserIdPhonePatchRequest,
  AdminUsersApi as V2Api,
} from "mws-identity-ts-sdk-v2-admin";
import { computed, type MaybeRef, type Ref, ref, unref } from "vue";

import type { AdminCognitoUsersResponse } from "@/users/api/types";

const identityApiUrl = import.meta.env.VITE_IDENTITY_API;
const identityV2Api = new V2Api(undefined, identityApiUrl);

export const currentUserQuery = queryOptions({
  queryKey: ["users", "current"],
  queryFn: async () => {
    const response = await identityV2Api.v2AdminUsersMeGet();

    return response.data;
  },
  staleTime: Infinity,
  retry: false,
});

export const useGetUserQuery = (
  userId: Ref<string | undefined>,
  { enabled }: { enabled: MaybeRef<boolean> } = {
    enabled: ref(true),
  },
) => {
  const queryKey = ["users", userId];

  const queryFn = async () => {
    if (!userId.value) throw new Error("userId is required");

    const response = await identityV2Api.v2AdminUsersUserIdGet({
      userId: userId.value,
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn, enabled });
};

// Uses raw axios (rather than the generated SDK) because the admin SDK has not
// been regenerated since this endpoint was added. Auth headers and v2
// case transformation are applied by the global axios interceptors in
// `src/plugins/http.ts`.
export const useGetUserCognitoQuery = (
  userId: Ref<string | undefined>,
  { enabled }: { enabled: MaybeRef<boolean> } = {
    enabled: ref(true),
  },
) => {
  const queryKey = ["users", userId, "cognito"];

  const queryFn = async () => {
    if (!userId.value) throw new Error("userId is required");

    const response = await axios.get<AdminCognitoUsersResponse>(
      `${identityApiUrl}/v2/admin/users/${userId.value}/cognito`,
    );

    return response.data;
  };

  return useQuery({ queryKey, queryFn, enabled });
};

export const useGetUsersCollectionQuery = (
  searchTerm: Ref,
  pageSize: Ref,
  offset: Ref,
  filters?: Ref<{
    roleIds: string[];
    xPlatformKey?: string;
  }>,
) => {
  const queryKey = ["users", { searchTerm, pageSize, offset, filters }];

  const queryFn = async () => {
    const response = await identityV2Api.v2AdminUsersGet({
      searchTerm: searchTerm.value,
      size: pageSize.value,
      offset: offset.value,
      ...(filters?.value || {}),
    });

    return response.data;
  };

  const options = { keepPreviousData: true };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetUserProxiesQuery = (
  userId: Ref<string | undefined>,
  phoneNumbers: MaybeRef<readonly (string | null | undefined)[]>,
) => {
  const sanitizedTerms = computed(() => {
    const deduplicated = new Set<string>();
    for (const value of unref(phoneNumbers)) {
      const trimmed = value?.trim();
      if (trimmed) deduplicated.add(trimmed);
    }

    return Array.from(deduplicated);
  });

  const enabled = computed(
    () => !!userId.value && sanitizedTerms.value.length > 0,
  );

  const queryKey = ["users", userId, "proxies", sanitizedTerms];

  const queryFn = async (): Promise<AdminListUser[]> => {
    const response = await identityV2Api.v2AdminUsersGet({
      searchTerms: sanitizedTerms.value,
      size: 50,
      offset: 0,
    });

    return (response.data.results ?? []).filter((u) => u.id !== userId.value);
  };

  return useQuery({ queryKey, queryFn, enabled });
};

export const useHardSuspendUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      const response =
        await identityV2Api.v2AdminUsersUserIdHardSuspendPatch(request);

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
    },
  });
};

export const useSoftSuspendUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      const response =
        await identityV2Api.v2AdminUsersUserIdSoftSuspendPatch(request);

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
    },
  });
};

export const useUnsuspendUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      const response =
        await identityV2Api.v2AdminUsersUserIdUnsuspendPatch(request);

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
    },
  });
};

export const useResetUserPasswordMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      const response = await identityV2Api.v2AdminUsersUserIdPasswordResetPost({
        userId: request.userId,
      });

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
      onSuccess();
    },
    onError,
  });
};

export const useDeleteUserMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      await identityV2Api.v2AdminUsersUserIdDelete({
        userId: request.userId,
      });
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
      onSuccess();
    },
    onError,
  });
};

export const useUpdateUserRolesMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string; roles: string[] }) => {
      const response = await identityV2Api.v2AdminUsersUserIdRolesPut({
        userId: request.userId,
        setUserRolesRequestDto: { roles: request.roles },
      });

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
      onSuccess();
    },
    onError,
  });
};

export const useSetUserPaymentVerifiedMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      const response =
        await identityV2Api.v2AdminUsersUserIdVerificationPaymentVerifiedPatch(
          request,
        );

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
      onSuccess();
    },
    onError,
  });
};

export const useSetUserIdVerifiedMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { userId: string }) => {
      const response =
        await identityV2Api.v2AdminUsersUserIdIdVerificationPatch(request);

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ["users", request.userId] });
      queryClient.invalidateQueries({ queryKey: ["bidder", request.userId] });
      onSuccess();
    },
    onError,
  });
};

export const useUpdatePhoneNumberMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  return useMutation({
    mutationFn: async (
      request: AdminUsersApiV2AdminUsersUserIdPhonePatchRequest,
    ) => {
      const response =
        await identityV2Api.v2AdminUsersUserIdPhonePatch(request);

      return response.data;
    },
    onSuccess,
    onError,
  });
};
